/**
 * Listing claims (FRIDGE-731) — what a filter LISTING says about itself,
 * read from the text we hold and never asserted: the NSF/ANSI standards it
 * names, whether it claims lead or PFAS reduction, its rated capacity in
 * gallons and its stated service life in months.
 *
 * Two sources, in order: the importer's spec fields when present
 * (`nsf_certifications`, `contaminants_reduced`, `capacity_gallons`), then
 * the listing title ("NSF 42 & 372 Certified", "6 Month/200 Gallon
 * Capacity", "Reduce PFAS"). `lifespan_months` in specs is NOT read — it sits
 * at 6 on 353 of 458 live rows, an importer default rather than a listing
 * claim; the title's own "6 Month" is a claim.
 *
 * A claim is a claim. A certification is verified only in NSF's listings;
 * every surface that renders these says so.
 */
export interface ListingClaims {
  /** NSF/ANSI standards the listing names — 42, 53, 58, 372, 401, 244. */
  nsf: number[]
  /** "NSF Certified" with no standard named. */
  nsfUnspecified: boolean
  reducesLead: boolean
  reducesPfas: boolean
  capacityGallons: number | null
  lifespanMonths: number | null
}

const KNOWN_NSF = new Set([42, 53, 58, 244, 372, 401])
const MAX_MONTHS = 24
const MAX_GALLONS = 5000

export const EMPTY_CLAIMS: ListingClaims = Object.freeze({ nsf: [], nsfUnspecified: false, reducesLead: false, reducesPfas: false, capacityGallons: null, lifespanMonths: null }) as ListingClaims

function numbersNear(text: string): number[] {
  // "NSF 42 & 372", "NSF/ANSI 42, 53", "NSF 401,53 Certified", "NSF-42".
  const out = new Set<number>()
  const re = /\bNSF(?:\s*\/\s*ANSI)?\s*[- ]?\s*((?:\d{2,3})(?:\s*(?:&|,|and|\/|\+)\s*\d{2,3})*)/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    for (const n of m[1].match(/\d{2,3}/g) ?? []) {
      const v = Number(n)
      if (KNOWN_NSF.has(v)) out.add(v)
    }
  }
  return [...out].sort((a, b) => a - b)
}

// Free-form mentions are not performance claims. In ambiguous or negative
// prose, hide the reduction row rather than attribute an affirmative claim.
const UNCERTAIN_REDUCTION = /\b(?:no|not|never|without|cannot|can't|doesn't|don't|isn't|aren't|won't|may|might|could|unverified|unproven|unknown|uncertain|unrated|non[- ]reducing|non[- ]removing|unlike|versus|compare)\b|\?/i
const MATERIAL_LEAD = /\blead\s*[- ]\s*free\b|\blead\s+(?:content|in\s+(?:the\s+)?materials?)\b/i
const CONTAMINANT = '(?:lead|PFAS|PFOA|PFOS|chlorine|chloramine|mercury|cysts?|asbestos|benzene|cadmium|copper|sediment|particulates?|taste|odou?r)'
const CONTAMINANT_LIST = `${CONTAMINANT}(?:\\s*(?:,\\s*(?:and\\s+)?|&|and|/)\\s*${CONTAMINANT})*`

function titleClaimsReduction(title: string, contaminant: RegExp): boolean {
  // Only an explicit reduction/removal phrase naming a contaminant qualifies.
  // Restrict the intervening words to contaminant lists, not arbitrary prose.
  const verbFirst = new RegExp(`\\b(?:reduces?|reduced|reducing|removes?|removed|removing|reduction\\s+of|removal\\s+of)\\s+(${CONTAMINANT_LIST})(?![\\w-])`, 'gi')
  const nounFirst = new RegExp(`\\b(${CONTAMINANT_LIST})\\s*[- ]\\s*(?:reduction|removal|reducing|removing)\\b`, 'gi')
  for (const pattern of [verbFirst, nounFirst]) {
    for (const match of title.matchAll(pattern)) {
      if (contaminant.test(match[1])) return true
    }
  }
  return false
}

function claimsReduction(title: string, entries: unknown, contaminant: RegExp, lead = false): boolean {
  // Retailer titles can use mathematical bold letters for the literal claim.
  // Compatibility normalization changes presentation, not the claimed fact.
  const normalizedTitle = title.normalize('NFKC')
  const structured = Array.isArray(entries) ? entries.filter((value): value is string => typeof value === 'string').map(value => value.normalize('NFKC')) : []
  const evidence = [normalizedTitle, ...structured].join(' ')
  // A contradictory title must not be overridden by a positive spec label.
  if (UNCERTAIN_REDUCTION.test(evidence) || (lead && MATERIAL_LEAD.test(evidence))) return false
  // The field names its meaning; accept exact contaminant labels, not a
  // coerced object, a negative sentence, or an unrelated word occurrence.
  const explicitLabel = structured.some(value => /^(?:lead|PFAS|PFOA|PFOS)$/i.test(value.trim()) && contaminant.test(value.trim()))
  return explicitLabel || titleClaimsReduction(normalizedTitle, contaminant)
}

/** Read the claims a listing makes, from its spec fields and its title. Pure. */
export function parseListingClaims(row: { title?: string | null; specs_json?: Record<string, unknown> | null }): ListingClaims {
  const title = typeof row.title === 'string' ? row.title : ''
  const specs = row.specs_json && typeof row.specs_json === 'object' ? row.specs_json : {}
  const nsf = new Set<number>()
  let nsfUnspecified = false

  const specNsf = specs['nsf_certifications']
  if (typeof specNsf === 'string' && specNsf.trim()) {
    for (const n of numbersNear(specNsf)) nsf.add(n)
    if (nsf.size === 0 && /nsf/i.test(specNsf)) nsfUnspecified = true
  }
  for (const n of numbersNear(title)) nsf.add(n)
  if (nsf.size === 0 && /\bNSF\b/i.test(title)) nsfUnspecified = true

  const reducesLead = claimsReduction(title, specs['contaminants_reduced'], /\blead\b/i, true)
  const reducesPfas = claimsReduction(title, specs['contaminants_reduced'], /\b(?:PFAS|PFOA|PFOS)\b/i)

  let capacityGallons: number | null = null
  const specCap = Number(specs['capacity_gallons'])
  if (Number.isFinite(specCap) && specCap > 0 && specCap <= MAX_GALLONS) capacityGallons = Math.round(specCap)
  if (capacityGallons === null) {
    const m = title.match(/\b(\d{2,4})\s*[- ]?\s*gallons?\b/i)
    if (m) {
      const v = Number(m[1])
      if (v > 0 && v <= MAX_GALLONS) capacityGallons = v
    }
  }

  let lifespanMonths: number | null = null
  const mm = title.match(/\b(\d{1,2})\s*[- ]?\s*months?\b/i)
  if (mm) {
    const v = Number(mm[1])
    if (v >= 1 && v <= MAX_MONTHS) lifespanMonths = v
  }

  return { nsf: [...nsf].sort((a, b) => a - b), nsfUnspecified, reducesLead, reducesPfas, capacityGallons, lifespanMonths }
}

/** True when the listing claims anything at all — the Real-or-Hidden gate for a claims row. */
export function hasAnyClaim(c: ListingClaims): boolean {
  return c.nsf.length > 0 || c.nsfUnspecified || c.reducesLead || c.reducesPfas || c.capacityGallons !== null || c.lifespanMonths !== null
}

/** "NSF/ANSI 42, 53" · "NSF (standard not stated)" · "" */
export function describeNsf(c: ListingClaims): string {
  if (c.nsf.length > 0) return `NSF/ANSI ${c.nsf.join(', ')}`
  if (c.nsfUnspecified) return 'NSF (standard not stated)'
  return ''
}
